// src/data/serviceFaqs.tsx

export interface FaqItem {
  question: string;
  answer: string;
  serviceId?: string; // Optional field to associate with specific service
}

export interface ServiceFaqGroup {
  id: string;
  title: string;
  description: string;
  faqs: FaqItem[];
}

export const serviceFaqs: ServiceFaqGroup[] = [
  {
    id: 'web',
    title: 'Web Development FAQs',
    description: 'Common questions about our web development services.',
    faqs: [
      {
        question: 'What technologies do you use for web development?',
        answer:
          "We use modern web technologies including React, Vue.js, Next.js, and TypeScript for frontend development. For backend, we work with Node.js, Python, and PHP frameworks like Laravel. Our approach is to select the best stack for each project's specific requirements.",
        serviceId: 'web',
      },
      {
        question: 'How long does it take to build a website?',
        answer:
          "Project timelines vary based on complexity, but generally: Simple websites (5-10 pages): 2-4 weeks; Medium-sized sites with custom features: 4-8 weeks; Complex platforms with integration needs: 8-12+ weeks. We'll provide a detailed timeline during our initial consultation.",
        serviceId: 'web',
      },
      {
        question: 'Do you offer website maintenance after launch?',
        answer:
          'Yes, we offer flexible maintenance packages to keep your site secure, updated, and performing optimally. Our support options include regular updates, security monitoring, performance optimization, and content updates, all tailored to your specific needs.',
        serviceId: 'web',
      },
      {
        question: 'Can you redesign my existing website?',
        answer:
          'Absolutely. We specialize in transforming outdated websites into modern, high-performing digital experiences. Our redesign process begins with a thorough analysis of your current site, identifying opportunities for improvement in design, functionality, and conversion optimization.',
        serviceId: 'web',
      },
      {
        question: 'How do you ensure websites are mobile-friendly?',
        answer:
          'We implement responsive design principles from the start, ensuring optimal viewing across all devices. Our development process includes testing on multiple device types and screen sizes. We utilize mobile-first approaches when appropriate and conduct performance optimization specifically for mobile users.',
        serviceId: 'web',
      },
    ],
  },
  {
    id: 'app',
    title: 'App Development FAQs',
    description:
      'Common questions about our mobile and web application development.',
    faqs: [
      {
        question: 'Do you develop native or cross-platform mobile apps?',
        answer:
          'We develop both, depending on your project requirements. For high-performance, platform-specific needs, we create native apps using Swift/Objective-C (iOS) and Kotlin/Java (Android). For faster deployment across platforms, we use React Native or Flutter to build cross-platform apps with near-native performance.',
        serviceId: 'app',
      },
      {
        question: 'How do you handle app store submissions?',
        answer:
          'We manage the entire submission process for both Apple App Store and Google Play Store. This includes preparing all required assets, optimizing listings for discoverability, ensuring compliance with store guidelines, and addressing any feedback during the review process.',
        serviceId: 'app',
      },
      {
        question: "What's your approach to app security?",
        answer:
          'Security is integrated throughout our development process. We implement secure authentication methods, data encryption, secure API communications, and follow OWASP guidelines. We also conduct security testing, vulnerability assessments, and keep dependencies updated to address emerging threats.',
        serviceId: 'app',
      },
      {
        question: 'How do you handle app updates and maintenance?',
        answer:
          "We provide ongoing maintenance services including bug fixes, compatibility updates for new OS versions, performance optimization, and feature enhancements. We typically recommend quarterly maintenance reviews and can establish a tailored maintenance schedule based on your application's complexity and user base.",
        serviceId: 'app',
      },
      {
        question: 'Can you integrate with existing systems and APIs?',
        answer:
          'Yes, integration capability is one of our core strengths. We have extensive experience connecting apps with various third-party services, enterprise systems, payment gateways, CRMs, ERPs, and custom APIs. We design flexible, maintainable integration architectures that adapt to evolving requirements.',
        serviceId: 'app',
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics Solutions FAQs',
    description:
      'Common questions about our analytics and data visualization services.',
    faqs: [
      {
        question:
          'How do you approach data privacy in analytics implementations?',
        answer:
          'We prioritize data privacy by implementing IP anonymization, respecting cookie consent, configuring data retention policies, and ensuring compliance with regulations like GDPR and CCPA. We follow data minimization principles, collecting only necessary information and providing clear documentation on data handling practices.',
        serviceId: 'analytics',
      },
      {
        question: 'Can you integrate with our existing analytics setup?',
        answer:
          'Absolutely. We can enhance your current analytics implementation, consolidate multiple tracking systems, or establish connections between different data sources. Our team has expertise in working with various analytics platforms and can develop custom integration solutions when needed.',
        serviceId: 'analytics',
      },
      {
        question: 'What types of custom dashboards do you create?',
        answer:
          'We build tailored dashboards using tools like Looker Studio, Tableau, Power BI, or custom web interfaces. These dashboards provide real-time visualization of key metrics, automated reporting, trend analysis, and user behavior insights. Each dashboard is designed around your specific KPIs and business objectives.',
        serviceId: 'analytics',
      },
      {
        question:
          'How do you handle the transition from Universal Analytics to GA4?',
        answer:
          'Our GA4 migration process includes parallel implementation alongside existing UA, property setup with enhanced configurations, custom event creation, audience definition, and conversion tracking. We provide training on the new interface and ensure proper historical data handling while maintaining reporting continuity.',
        serviceId: 'analytics',
      },
      {
        question:
          'Can you help with attribution modeling and conversion tracking?',
        answer:
          'Yes, we implement multi-touch attribution models to properly credit touchpoints in the customer journey. Our conversion tracking solutions span across platforms using server-side tracking when appropriate, ensuring accurate measurement even with browser privacy limitations and ad-blockers.',
        serviceId: 'analytics',
      },
    ],
  },
  {
    id: 'ai',
    title: 'AI Solutions FAQs',
    description:
      'Common questions about our artificial intelligence and machine learning services.',
    faqs: [
      {
        question: 'What types of AI solutions do you implement?',
        answer:
          'We develop a wide range of AI solutions including predictive analytics models, natural language processing systems, recommendation engines, computer vision applications, and automated decision systems. Our focus is on practical AI implementations that deliver measurable business value.',
        serviceId: 'ai',
      },
      {
        question: 'Do you build custom AI models or use existing services?',
        answer:
          'We do both depending on project requirements. For efficiency, we leverage established AI services from providers like OpenAI, Google, and Azure when appropriate. For unique challenges requiring specialized solutions, we develop and train custom models tailored to your specific data and objectives.',
        serviceId: 'ai',
      },
      {
        question:
          'How much data is needed to implement an effective AI solution?',
        answer:
          "Data requirements vary by project type. While more quality data typically improves outcomes, we can often start with modest datasets using transfer learning and pre-trained models. For custom solutions, we'll assess your available data during discovery and recommend acquisition strategies if needed.",
        serviceId: 'ai',
      },
      {
        question: 'How do you address bias and ethical concerns in AI?',
        answer:
          'We follow responsible AI practices including bias detection in training data, regular algorithmic auditing, transparent model documentation, and ongoing monitoring for unexpected outcomes. We implement appropriate governance frameworks and design systems with human oversight where necessary.',
        serviceId: 'ai',
      },
      {
        question: "What's the typical ROI timeframe for AI investments?",
        answer:
          'ROI timelines vary by implementation type. Process automation typically yields returns within 3-6 months. Predictive models and recommendation systems often show meaningful results in 6-12 months. We establish clear success metrics at project start and track performance against these benchmarks.',
        serviceId: 'ai',
      },
    ],
  },
  {
    id: 'automation',
    title: 'Automation FAQs',
    description:
      'Common questions about our business process and workflow automation services.',
    faqs: [
      {
        question: 'What business processes can you automate?',
        answer:
          'We automate a wide range of processes including document management, approval workflows, data entry and validation, customer communications, reporting, scheduling, inventory management, and cross-system data synchronization. Our approach focuses on identifying high-value automation opportunities that deliver significant efficiency gains.',
        serviceId: 'automation',
      },
      {
        question: 'How do you identify automation opportunities?',
        answer:
          'We conduct process audits to identify repetitive, rule-based tasks consuming significant time. Our assessment examines error rates, bottlenecks, and resource allocation. We prioritize automation candidates based on potential time savings, error reduction, and ROI, creating a strategic automation roadmap.',
        serviceId: 'automation',
      },
      {
        question:
          'Do you use no-code/low-code platforms or custom development?',
        answer:
          'We utilize both approaches depending on requirements. For straightforward processes, we leverage no-code/low-code platforms like Zapier, Power Automate, or Airtable for rapid deployment and lower maintenance. For complex, high-volume processes, we develop custom automation solutions offering greater flexibility and scalability.',
        serviceId: 'automation',
      },
      {
        question:
          'How do you ensure smooth adoption of new automated workflows?',
        answer:
          'Our implementation process includes comprehensive training, detailed documentation, initial monitoring, and a phased rollout approach. We focus on user experience design within automated systems and provide support during the transition period to address questions and make adjustments as needed.',
        serviceId: 'automation',
      },
      {
        question: 'How do you measure the success of automation initiatives?',
        answer:
          'We establish baseline metrics before implementation and track key indicators including time savings, error reduction, throughput increases, and cost reduction. Our automation solutions include built-in analytics to provide visibility into process performance and identify ongoing optimization opportunities.',
        serviceId: 'automation',
      },
    ],
  },
  {
    id: 'whatsapp-business-solutions',
    title: 'WhatsApp Business Integration FAQs',
    description:
      'Common questions about our WhatsApp Business Platform (Cloud API) integration services.',
    faqs: [
      {
        question:
          'What exactly do you build with the WhatsApp Business Platform?',
        answer:
          "We build integrations on Meta's WhatsApp Business Platform (Cloud API) that connect a client's WhatsApp Business number to the systems they already use, such as a CRM, helpdesk, or internal dashboard. This typically includes two-way messaging, automated notifications like order confirmations and appointment reminders, and rules that route incoming conversations to the right team or agent.",
        serviceId: 'whatsapp-business-solutions',
      },
      {
        question: 'Who owns the WhatsApp Business Account and phone number?',
        answer:
          'The client always owns their WhatsApp Business Account (WABA), phone number, and Meta Business Manager. We act as a technology provider that builds and configures the integration on their behalf. We do not take ownership of client accounts, phone numbers, or end-customer data at any point.',
        serviceId: 'whatsapp',
      },
      {
        question: 'What customer data do you access, and how is it handled?',
        answer:
          "Our integrations process only the message data required for the feature being built, such as message content, sender number, and delivery status, and it flows directly between Meta's Cloud API and the client's own systems (CRM, helpdesk, or database). We do not sell, share, or use this data for advertising or any purpose outside the scope of the client's service. Full details are in our Privacy Policy.",
        serviceId: 'whatsapp',
      },
      {
        question: 'Do you support message templates and opt-in requirements?',
        answer:
          "Yes. We help clients set up approved message templates for notifications and reminders, and we build opt-in flows so that end customers explicitly consent to receive WhatsApp messages before any business-initiated conversation begins. This keeps implementations aligned with Meta's WhatsApp Business Messaging Policy.",
        serviceId: 'whatsapp',
      },
      {
        question: 'Can WhatsApp Business App and Cloud API run side by side?',
        answer:
          'Yes. Many clients want to keep using the WhatsApp Business App for day-to-day conversations while automated systems run through the Cloud API. We configure coexistence so both can operate on the same number without disrupting existing workflows or historical chat history.',
        serviceId: 'whatsapp',
      },
      {
        question: 'What kind of businesses is this integration built for?',
        answer:
          'We work with small and mid-sized businesses across e-commerce, services, healthcare scheduling, and logistics that want to centralize WhatsApp conversations instead of managing them from a phone. Typical outcomes include faster response times, fewer missed messages, and automated status updates that reduce manual follow-up.',
        serviceId: 'whatsapp',
      },
    ],
  },
  {
    id: 'general',
    title: 'General Project FAQs',
    description:
      'Common questions about our project process, timelines, and collaboration approach.',
    faqs: [
      {
        question: "What's your typical project process?",
        answer:
          'Our projects follow a structured approach: 1) Discovery & Planning - Understanding requirements and creating a roadmap; 2) Design & Prototyping - Creating the solution architecture and user experience; 3) Development - Building in iterative cycles with regular reviews; 4) Testing & QA - Ensuring quality and performance; 5) Deployment - Launching the solution; 6) Support & Optimization - Providing ongoing maintenance and improvements.',
      },
      {
        question: 'How do you handle project management and communication?',
        answer:
          'We use agile methodologies with weekly sprints and regular check-ins. Each client receives access to our project management platform for real-time progress tracking. We establish clear communication channels including a dedicated project manager as your main point of contact, scheduled update meetings, and documentation of all key decisions.',
      },
      {
        question: 'What if I need changes to the project scope?',
        answer:
          'We understand that requirements evolve. Our process incorporates flexibility through a structured change management approach. Requested changes are assessed for impact on timeline, resources, and budget. Minor adjustments are often accommodated within sprint planning, while significant changes are documented with clear implications before proceeding.',
      },
      {
        question: 'Do you offer fixed-price or time-and-materials pricing?',
        answer:
          'We offer both pricing models depending on project nature. Fixed-price works well for clearly defined projects with stable requirements. Time-and-materials is ideal for complex or evolutionary projects needing flexibility. We also offer hybrid models with a fixed base scope and flexible enhancement options.',
      },
      {
        question: 'What happens after the project is complete?',
        answer:
          'Project completion includes a formal handover with knowledge transfer, documentation, and training. We offer various support options from basic maintenance to comprehensive managed services. Many clients choose our ongoing partnership plans that include regular updates, performance monitoring, and strategic technology planning.',
      },
    ],
  },
];

// Helper function to get FAQs for a specific service ID
export const getFaqsByServiceId = (serviceId: string): FaqItem[] => {
  // Get the specific service FAQs
  const serviceFaqGroup = serviceFaqs.find((group) => group.id === serviceId);
  const specificFaqs = serviceFaqGroup?.faqs || [];

  // Get the general FAQs that apply to all services
  const generalFaqs =
    serviceFaqs.find((group) => group.id === 'general')?.faqs || [];

  // Combine and return both sets of FAQs
  return [...specificFaqs, ...generalFaqs];
};

// Get all FAQs
export const getAllFaqs = (): FaqItem[] => {
  return serviceFaqs.flatMap((group) => group.faqs);
};
